import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TableModule} from 'primeng/table';
import {CreditNoteService} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';

@Component({
  selector: 'elcia-credit-note-preview',
  imports: [TranslatePipe, DecimalPipe, TableModule, ElciaCard, Loader],
  templateUrl: './credit-note-preview.html',
  styleUrl: './credit-note-preview.scss'
})
export class CreditNotePreview {
  readonly creditNoteId = input.required<number | null>();

  private readonly creditNoteService = inject(CreditNoteService);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly hasCreditNote = computed(() => this.creditNoteId() != null);

  private readonly pdfQuery = injectQuery(() => {
    const id = this.creditNoteId();
    return {
      queryKey: ['creditNotePdf', id],
      queryFn: () => lastValueFrom(this.creditNoteService.getCreditNotePdf(id!)),
      enabled: id != null,
    };
  });

  private readonly accountingQuery = injectQuery(() => {
    const id = this.creditNoteId();
    return {
      queryKey: ['creditNoteAccountingOperations', id],
      queryFn: () => lastValueFrom(this.creditNoteService.getCreditNoteAccountingOperations(id!)),
      enabled: id != null,
    };
  });

  protected readonly isPdfLoading = computed(() => this.pdfQuery.isLoading());
  protected readonly isPdfError = computed(() => this.pdfQuery.isError());

  protected readonly isAccountingLoading = computed(() => this.accountingQuery.isLoading());
  protected readonly operations = computed(() => this.accountingQuery.data()?.accountingOperations ?? []);
  protected readonly totalDebit = computed(() => this.operations().reduce((sum, op) => sum + op.debit, 0));
  protected readonly totalCredit = computed(() => this.operations().reduce((sum, op) => sum + op.credit, 0));

  protected readonly pdfUrl = signal<SafeResourceUrl | null>(null);

  private readonly bindPdfUrl = effect(onCleanup => {
    const blob = this.pdfQuery.data();
    if (!blob) {
      this.pdfUrl.set(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(`${objectUrl}#pagemode=none`));
    onCleanup(() => URL.revokeObjectURL(objectUrl));
  });
}
